/**
 * Working Draft reducer (pure).
 *
 * The Working Draft is the unsaved template the superadmin and the AI co-edit.
 * It is diffed against the active Prompt Version and promoted only on human
 * Save. AI turns and manual edits stack; the human can undo the last turn or
 * discard back to the active version. Keeping this a pure state machine makes
 * apply/undo/discard testable without the UI.
 */

export interface WorkingDraftState {
  /** The active Prompt Version's template — the diff/discard baseline. */
  base: string;
  /** The current Working Draft text. */
  current: string;
  /** Prior `current` values, oldest first; the undo stack. */
  history: string[];
}

export type WorkingDraftAction =
  | { type: "applyAiTurn"; template: string }
  | { type: "manualEdit"; text: string }
  | { type: "undoLastTurn" }
  | { type: "discardAll" }
  // Re-base onto a new active version (on load, or "discard & re-base" after a
  // concurrency conflict): both base and current become `base`, history clears.
  | { type: "reset"; base: string };

export function initWorkingDraft(base: string): WorkingDraftState {
  return { base, current: base, history: [] };
}

export function workingDraftReducer(
  state: WorkingDraftState,
  action: WorkingDraftAction,
): WorkingDraftState {
  switch (action.type) {
    case "applyAiTurn":
      return { ...state, current: action.template, history: [...state.history, state.current] };
    case "manualEdit":
      return { ...state, current: action.text, history: [...state.history, state.current] };
    case "undoLastTurn": {
      if (state.history.length === 0) return state;
      const history = state.history.slice(0, -1);
      const current = state.history[state.history.length - 1];
      return { ...state, current, history };
    }
    case "discardAll":
      return { ...state, current: state.base, history: [] };
    case "reset":
      return initWorkingDraft(action.base);
    default:
      return state;
  }
}
